/**
 * Wrapper typed peste endpoint-urile /auth. Fluxul crypto se întâmplă în
 * componentele care apelează aceste funcții — serverul vede doar blob-uri
 * base64, niciodată parola master.
 */

import { api } from '@/api/client';

export interface KdfParams {
  algorithm: 'argon2id';
  mem_kib: number;
  iters: number;
  parallelism: number;
  key_len: number;
}

export interface SaltResponse {
  kdf_salt: string;
  kdf_params: KdfParams;
}

export interface RecoveryStatus {
  enabled: boolean;
  set_at: string | null;
}

/** Ce produce clientul pentru o frază — vezi `buildRecoverySetup`. */
export interface RecoveryBundle {
  recovery_kdf_salt: string;
  recovery_auth_hash: string;
  recovery_private_key_encrypted: string;
  recovery_private_key_nonce: string;
  recovery_phrase_sealed: string;
}

export interface RecoverySetupRequest extends RecoveryBundle {
  auth_hash: string;
}

export interface RecoveryStartResponse {
  recovery_private_key_encrypted: string;
  recovery_private_key_nonce: string;
  requires_2fa: boolean;
}

export interface RecoveryCompleteRequest {
  email: string;
  recovery_auth_hash: string;
  auth_hash_new: string;
  kdf_salt: string;
  kdf_params: KdfParams;
  private_key_encrypted: string;
  private_key_nonce: string;
  totp_code?: string;
  recovery_code?: string;
  /** Fraza folosită se consumă: recuperarea vine cu una nouă. */
  recovery: RecoveryBundle;
}

export type SessionLifetime = 10 | 15 | 30 | 60 | 90 | 120;

// `labelKey` e o cheie i18n — se traduce la randare, cu `t(o.labelKey)`.
export const SESSION_LIFETIME_OPTIONS: Array<{ value: SessionLifetime; labelKey: string }> = [
  { value: 10, labelKey: 'shell_ui.session_lifetime.m10' },
  { value: 15, labelKey: 'shell_ui.session_lifetime.m15' },
  { value: 30, labelKey: 'shell_ui.session_lifetime.m30' },
  { value: 60, labelKey: 'shell_ui.session_lifetime.m60' },
  { value: 90, labelKey: 'shell_ui.session_lifetime.m90' },
  { value: 120, labelKey: 'shell_ui.session_lifetime.m120' },
];

export interface RegisterRequest {
  email: string;
  kdf_params: KdfParams;
  kdf_salt: string;
  auth_hash: string;
  public_key: string;
  private_key_encrypted: string;
  private_key_nonce: string;
  tenant_name?: string;
  tenant_slug?: string;
  session_lifetime_minutes?: SessionLifetime;
  /** Fraza de recuperare se generează automat la înregistrare. */
  recovery: RecoveryBundle;
}

export interface RegisterResponse {
  user_id: string;
  tenant_id: string;
  session_expires_at: string;
}

export interface LoginRequest {
  email: string;
  auth_hash: string;
  totp_code?: string;
  recovery_code?: string;
  session_lifetime_minutes?: SessionLifetime;
}

export interface TenantMembership {
  tenant_id: string;
  name: string;
  slug: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
}

export interface LoginResponse {
  user_id: string;
  private_key_encrypted: string;
  private_key_nonce: string;
  tenants: TenantMembership[];
  session_expires_at: string;
  recovery_codes_remaining?: number | null;
}

export interface LoginTwoFactorRequired {
  requires_2fa: true;
  user_id: string;
}

export type LoginResponseOrChallenge = LoginResponse | LoginTwoFactorRequired;

export function isTwoFactorRequired(
  resp: LoginResponseOrChallenge,
): resp is LoginTwoFactorRequired {
  return (resp as LoginTwoFactorRequired).requires_2fa === true;
}

export interface TwoFactorStatus {
  enabled: boolean;
  enabled_at?: string | null;
  recovery_codes_remaining: number;
}

export interface TwoFactorSetupResponse {
  secret_b32: string;
  otpauth_url: string;
  setup_token: string;
  issuer: string;
  account: string;
}

export interface TwoFactorConfirmRequest {
  setup_token: string;
  totp_code: string;
}

export interface TwoFactorConfirmResponse {
  recovery_codes: string[];
}

export interface TwoFactorDisableRequest {
  auth_hash: string;
  totp_code?: string;
  recovery_code?: string;
}

export interface RecoveryCodesRegenerateRequest {
  auth_hash: string;
  totp_code: string;
}

export interface SessionInfo {
  id: string;
  user_agent: string | null;
  ip: string | null;
  created_at: string;
  last_active_at: string;
  expires_at: string;
  is_current: boolean;
}

export interface ChangePasswordRequest {
  auth_hash_old: string;
  auth_hash_new: string;
  kdf_salt: string;
  kdf_params: KdfParams;
  private_key_encrypted: string;
  private_key_nonce: string;
  /** Fraza de recuperare se rotește odată cu parola master. */
  recovery: RecoveryBundle;
}

export const authApi = {
  getSalt: (email: string) =>
    api.get<SaltResponse>(`/auth/salt?email=${encodeURIComponent(email)}`),
  /** Parametrii KDF ai userului autentificat — fără rate limit-ul de login. */
  /** Contul curent cu lista de spații la zi. */
  me: () =>
    api.get<{
      user_id: string;
      email: string;
      tenants: TenantMembership[];
      is_global_admin: boolean;
    }>('/auth/me'),
  getOwnKdf: () => api.get<SaltResponse>('/auth/kdf'),

  // --- Recuperare cu frază (vezi crypto/recovery.ts) ---
  recoveryStatus: () => api.get<RecoveryStatus>('/auth/recovery/status'),
  recoverySetup: (body: RecoverySetupRequest) =>
    api.post<RecoveryStatus>('/auth/recovery/setup', body),
  recoveryPhrase: (auth_hash: string) =>
    api.post<{ recovery_phrase_sealed: string }>('/auth/recovery/phrase', { auth_hash }),
  recoveryParams: (email: string) =>
    api.get<SaltResponse>(`/auth/recovery/params?email=${encodeURIComponent(email)}`),
  recoveryStart: (email: string, recovery_auth_hash: string) =>
    api.post<RecoveryStartResponse>('/auth/recovery/start', { email, recovery_auth_hash }),
  recoveryComplete: (body: RecoveryCompleteRequest) =>
    api.post<null>('/auth/recovery/complete', body),

  register: (req: RegisterRequest) =>
    api.post<RegisterResponse>('/auth/register', req),

  login: (req: LoginRequest) => api.post<LoginResponseOrChallenge>('/auth/login', req),

  logout: () => api.post<null>('/auth/logout'),

  heartbeat: () => api.post<{ expires_at: string }>('/auth/heartbeat'),

  twoFactorStatus: () => api.get<TwoFactorStatus>('/auth/2fa/status'),

  twoFactorSetup: () => api.post<TwoFactorSetupResponse>('/auth/2fa/setup'),

  twoFactorConfirm: (req: TwoFactorConfirmRequest) =>
    api.post<TwoFactorConfirmResponse>('/auth/2fa/setup/confirm', req),

  twoFactorDisable: (req: TwoFactorDisableRequest) =>
    api.post<null>('/auth/2fa/disable', req),

  twoFactorRegenerateRecovery: (req: RecoveryCodesRegenerateRequest) =>
    api.post<TwoFactorConfirmResponse>('/auth/2fa/recovery-codes/regenerate', req),

  changePassword: (req: ChangePasswordRequest) =>
    api.post<null>('/auth/change-password', req),

  listSessions: () => api.get<SessionInfo[]>('/auth/sessions'),

  revokeSession: (id: string) => api.delete<null>(`/auth/sessions/${id}`),

  revokeAllOtherSessions: () =>
    api.delete<{ revoked: number }>('/auth/sessions'),
};
